<script setup lang="ts">
  import JSONEditor from 'jsoneditor'
  import { onBeforeUnmount, onMounted, onUnmounted, ref, watch } from 'vue'
  import { Button, message, Spin } from 'ant-design-vue'
  import { DEFAULT_INPUT_DEBOUNCE_MS, defaultMessageDurationSeconds } from '@/utils'
  import { JsonEditorSchemaType } from './models/JsonEditorCdnSchemaType'
  import { SchemaStoreService } from '../services/SchemaStoreService'
  import { SchemaService } from '../services/SchemaService'
  import { InjectionService } from '../services/InjectionService'
  import { DeploymentPrefix } from '../models/DeploymentPrefix'
  import { ISchema } from '../models/ISchema'
  import JsonEditorSchemaFactory from './services/JsonEditorSchemaFactory'
  import IJsonEditorSchemaGenerator from './services/IJsonEditorSchemaGenerator'
  import Ajv from 'ajv/dist/2019'
  import { CdnSchemaJsonValidator } from './services/CdnSchemaJsonValidator'
  import { cdnSchemaStoreTypeMap } from '../CdnSchema/models/CdnSchemaStoreTypeMap'
  import { UploadOutlined } from '@ant-design/icons-vue'
  import { JsonEditorSchemaCoalesceService } from './services/JsonEditorSchemaCoaleseService'
  import { debounceTime, Subject } from 'rxjs'

  interface IJsonEditorSchemaConfigurationProps {
    schemaType: JsonEditorSchemaType
    modelValue: Record<string, any>
    configurationLevel: number
    preventModelUpdateOnValidationError?: boolean
    isProcessingEntity?: boolean
  }

  interface IJsonEditorSchemaConfigurationEmits {
    (e: 'update:modelValue', value: Record<string, any>): void
  }

  const customSchemaStore = SchemaStoreService.useCustomSchemaStore()

  const props = defineProps<IJsonEditorSchemaConfigurationProps>()
  const emit = defineEmits<IJsonEditorSchemaConfigurationEmits>()

  const isLoadingSchemas = ref<boolean>(false)
  const isProcessingEntity = ref<boolean>(props.isProcessingEntity)
  const modelValue = ref<Record<string, any>>({ ...(props.modelValue ?? {}) })
  // Ref for the hidden file input
  const fileInput = ref<HTMLInputElement | null>(null)
  const importedJson = ref<string | undefined>()

  // Prevent uneccessary remount of component
  const suppressNextModelValueChangeUpdate = ref(false)
  const notifyConfigChangedStream$ = new Subject<any>()
  const notifyConfigChangedStreamSubscription = notifyConfigChangedStream$
    .pipe(debounceTime(DEFAULT_INPUT_DEBOUNCE_MS))
    .subscribe((value: any) => {
      modelValue.value = value
    })

  watch(
    () => props.modelValue,
    (newVal) => {
      if (suppressNextModelValueChangeUpdate.value) {
        suppressNextModelValueChangeUpdate.value = false
        return
      }
      modelValue.value = { ...(newVal ?? {}) }
      // Optionally update the editor if needed
      setJSONEditorModel(modelValue.value)
    },
    { deep: true }
  )

  watch(
    modelValue,
    (newVal) => {
      suppressNextModelValueChangeUpdate.value = true
      emit('update:modelValue', newVal)
    },
    { deep: true }
  )

  watch(
    () => props.isProcessingEntity,
    (val) => {
      isProcessingEntity.value = val ?? false
    },
    { immediate: true }
  )

  const editorContainer = ref<HTMLElement | null>(null)
  let editor: any = null

  const schemaLocation = ref<DeploymentPrefix>()
  const storedSchemas = ref<Array<ISchema>>()

  const resolveSchemaStructuresForSchemaType = async (
    schemaType: JsonEditorSchemaType
  ): Promise<{
    schemaService: SchemaService<any>
    cdnSchemas: Array<ISchema>
    generator: IJsonEditorSchemaGenerator<any>
  }> => {
    const mapping = cdnSchemaStoreTypeMap[schemaType]
    if (!mapping) {
      throw new Error(`No registered schema mapping for ${schemaType}`)
    }

    const generator = JsonEditorSchemaFactory.get(schemaType)

    // Inject the service by the configured injectable key
    const schemaService = InjectionService.injectStrict(mapping.injectableKey) as SchemaService<any>

    // Read the store properties dynamically (type-checked via keyof)
    schemaLocation.value = customSchemaStore[mapping.storeLocationProp] as
      | DeploymentPrefix
      | undefined
    storedSchemas.value = customSchemaStore[mapping.storeSchemasProp] as Array<ISchema> | undefined

    // If user has configured custom location and provided schemas, use them; otherwise fetch from CDN/service
    const cdnSchemas =
      schemaLocation.value === DeploymentPrefix.Custom &&
      storedSchemas &&
      storedSchemas.value?.length
        ? storedSchemas.value
        : ((await schemaService.getSchemas(schemaLocation.value)) as Array<ISchema>)

    if (typeof cdnSchemas === 'string') throw Error('error getting schemas!')
    storedSchemas.value = cdnSchemas

    return { schemaService, cdnSchemas, generator }
  }

  const initializeJSONEditor = async () => {
    if (editorContainer.value) {
      isLoadingSchemas.value = true

      const schemaStructures = await resolveSchemaStructuresForSchemaType(props.schemaType)
      const jsonEditorSchema = schemaStructures.generator.generate(schemaStructures.cdnSchemas)

      const ajv = new Ajv({ allErrors: true })
      const validate = ajv.compile(jsonEditorSchema)

      let currentValidationErrorCount = 0

      editor = new JSONEditor(editorContainer.value, {
        mode: 'code',
        onValidate: (json: any) => {
          const valid = validate(json)
          if (valid) {
            currentValidationErrorCount = 0
            return []
          }
          const filtered = (validate.errors ?? []).filter(
            (err: any) => !['if', 'then'].includes(err.keyword)
          )
          currentValidationErrorCount = filtered.length
          return filtered.map((err: any) => ({
            path: err.instancePath
              ? err.instancePath
                  .split('/')
                  .filter(Boolean)
                  .map((s: string) => (isNaN(Number(s)) ? s : Number(s)))
              : [],
            message:
              err.keyword === 'additionalProperties'
                ? `${err.message}: '${err.params?.additionalProperty}'`
                : err.keyword === 'unevaluatedProperties'
                  ? `${err.message}: '${err.params?.unevaluatedProperty}'`
                  : err.message ?? 'Validation error',
            type: 'error'
          }))
        },
        onError: (err: Error) => console.error('Error:', err),
        onChange: () => {
          try {
            const newVal = editor.get()
            if (
              props.preventModelUpdateOnValidationError === true &&
              currentValidationErrorCount !== 0
            )
              return
            onEditorChangeForField(newVal)
          } catch (err: any) {
            const editorValue = editor.getText?.() ?? ''
            if (editorValue.trim() === '') {
              message.error('Editor content cannot be empty.', defaultMessageDurationSeconds)
            }
          }
        }
      })
      setJSONEditorModel(modelValue.value)
      editor.focus()
      isLoadingSchemas.value = false
    }
  }

  const onEditorChangeForField = (editorValue: any) => {
    notifyConfigChangedStream$.next(editorValue)
  }

  const setJSONEditorModel = (value: Record<string, any>) => {
    if (editor != null) {
      editor.set(value)
      if (editor.getMode() === 'tree') editor.expandAll()
    }
  }

  const importJsonFile = () => {
    // Reset JSON Editor
    fileInput.value?.click()
  }

  const handleFileSelected = (event: Event) => {
    const input = event.target as HTMLInputElement
    const file = input.files?.[0]

    if (file) {
      // Optional: validate file type
      if (!file.name.endsWith('.json')) {
        message.error('Please select a valid .json file', defaultMessageDurationSeconds)
        return
      }

      const reader = new FileReader()
      reader.onload = () => {
        importedJson.value = reader.result as string
        const result = updateJsonEditorContentFromJsonString(importedJson.value)
        if (result === true)
          message.success('JSON parsed successfully.', defaultMessageDurationSeconds)
      }
      reader.readAsText(file)
    }

    // Reset input value so the same file can be selected again later
    input.value = ''
  }

  const updateJsonEditorContentFromJsonString = (json: string): boolean => {
    if (json == null) throw new Error('valid json required')

    const result = CdnSchemaJsonValidator.validateJsonForCdnSchemaType(
      props.schemaType,
      json,
      storedSchemas.value!
    )
    if (!result.valid) {
      message.error(
        `Error parsing JSON: ${result.errors?.join(', ')}`,
        defaultMessageDurationSeconds
      )
      return false
    } else {
      const coalescedResult =
        JsonEditorSchemaCoalesceService.coalesceParsedResultToIQueueExecutionProp(
          props.schemaType,
          result
        )
      setJSONEditorModel(coalescedResult)
      onEditorChangeForField(coalescedResult)
      return true
    }
  }

  onMounted(async () => {
    await initializeJSONEditor()
  })

  onBeforeUnmount(() => {
    notifyConfigChangedStreamSubscription?.unsubscribe()
  })

  onUnmounted(() => {
    if (editor) {
      editor.destroy()
    }
  })
</script>

<template>
  <div class="flex flex-col">
    <!-- Hidden file input -->
    <input
      type="file"
      ref="fileInput"
      accept=".json"
      class="!hidden"
      @change="handleFileSelected"
    />
    <div
      v-if="isLoadingSchemas"
      class="absolute z-[1000] mt-[200px] flex w-full items-center justify-center"
    >
      <div :class="['py-8', 'text-center']">
        <Spin size="large" />
        <p class="mt-2 text-gray-500">Loading Schemas...</p>
      </div>
    </div>
    <div>
      <div class="mb-2 mr-2 mt-[-35px] flex w-full justify-end">
        <Button
          type="primary"
          :disabled="isLoadingSchemas || isProcessingEntity"
          class="flex items-center"
          @click="importJsonFile"
        >
          <UploadOutlined />
          Import JSON
        </Button>
      </div>

      <div ref="editorContainer" style="min-height: 500px; height: 500px"></div>
    </div>
  </div>
</template>
